// frontend/src/settings/PasswordPrefixSettings.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';
import Header from '../components/ui/Header';

function PasswordPrefixSettings() {
  const { currentOrganization } = useOrganization();
  const [prefix, setPrefix] = useState('hospital');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentOrganization?.id) fetchSettings();
  }, [currentOrganization]);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('holiday_policies')
        .select('initial_password_prefix')
        .eq('organization_id', currentOrganization.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      if (data?.initial_password_prefix) setPrefix(data.initial_password_prefix);
    } catch (error) {
      console.error('設定読み込みエラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!prefix.trim()) {
      alert('プレフィックスを入力してください');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from('holiday_policies')
        .upsert({
          organization_id: currentOrganization.id,
          initial_password_prefix: prefix.trim(),
          updated_at: new Date().toISOString(),
        }, { onConflict: 'organization_id' });

      if (error) throw error;
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('設定保存エラー:', error);
      alert('保存に失敗しました: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const styles = {
    container: { padding: '2rem', maxWidth: '600px', margin: '0 auto', fontFamily: 'sans-serif' },
    backLink: { display: 'inline-block', marginBottom: '1rem', color: '#007bff', textDecoration: 'none' },
    card: { backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', padding: '2rem', marginTop: '1.5rem', border: '1px solid #e0e0e0' },
    header: { borderBottom: '1px solid #eee', paddingBottom: '1rem', marginBottom: '1.5rem' },
    label: { display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333' },
    input: { width: '100%', padding: '0.6rem', borderRadius: '4px', border: '1px solid #ccc', fontSize: '1rem', boxSizing: 'border-box' },
    helpText: { fontSize: '0.85rem', color: '#666', marginTop: '0.4rem' },
    preview: { marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #dee2e6' },
    previewLabel: { fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' },
    previewValue: { fontSize: '1rem', fontWeight: '600', color: '#333', fontFamily: 'monospace' },
    buttonContainer: { marginTop: '2rem', display: 'flex', justifyContent: 'flex-end' },
    button: { padding: '0.75rem 1.5rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', fontSize: '1rem', cursor: 'pointer' },
    buttonSuccess: { backgroundColor: '#28a745' },
    infoBox: { backgroundColor: '#fff3cd', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #ffc107' },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <Header />
        <div style={{ textAlign: 'center', padding: '2rem' }}>読み込み中...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Header />
      <Link to="/settings" style={styles.backLink}>← 設定メニューへ戻る</Link>

      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={{ margin: 0 }}>初期パスワード設定</h2>
        </div>

        <div style={styles.infoBox}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#856404' }}>
            ⚠️ この設定は新規スタッフ追加時の初期パスワードに適用されます。<br />
            既存スタッフのパスワードは変更されません。
          </p>
        </div>

        <div>
          <label style={styles.label}>パスワードプレフィックス</label>
          <input
            type="text"
            value={prefix}
            onChange={e => setPrefix(e.target.value)}
            style={styles.input}
            placeholder="例: Marianna"
          />
          <p style={styles.helpText}>
            初期パスワードは「プレフィックス + 社員番号」の形式になります。
          </p>

          <div style={styles.preview}>
            <div style={styles.previewLabel}>プレビュー（社員番号: 2100001 の場合）</div>
            <div style={styles.previewValue}>{prefix || '(未設定)'}{'2100001'}</div>
          </div>
        </div>

        <div style={styles.buttonContainer}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ ...styles.button, ...(saved ? styles.buttonSuccess : {}), cursor: saving ? 'not-allowed' : 'pointer' }}
          >
            {saving ? '保存中...' : saved ? '✓ 保存しました' : '設定を保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PasswordPrefixSettings;